import { ArrowUpTrayIcon } from "@heroicons/react/24/outline"
import dayjs from "dayjs"
import React, { useMemo, useRef, useState } from "react"

import QuillEditor from "../../components/shared/QuillEditor"
import { useToast } from "../../contexts/ToastContext"
import { blogPostDateInputValue } from "../Blog/blogDateUtils"
import ImagePickerV2 from "../WebsiteBuilderV2/components/shared/ImagePickerV2"

import {
  BLOG_INLINE_IMAGE_MAX,
  BLOG_INLINE_MAX_BYTES,
  inlineImageUrlFromPicker,
  stripUnsafeInlineImagesFromHtml,
  uploadFileToFilestack
} from "./blogInlineImage"
import BlogPostActionBar from "./BlogPostActionBar"
import BlogPostFormField from "./BlogPostFormField"
import BlogPostTagInput from "./BlogPostTagInput"

const inputClassName =
  "w-full rounded-lg border border-[#c8d6df] bg-white px-3 py-2 text-sm text-[#0b3954] outline-none placeholder:text-[#c8d6df] focus:border-teal focus:ring-1 focus:ring-teal"

// Large phone photos + Filestack forced-crop were failing intermittently for pros.
// Skip crop, downscale oversized images, and cap file size for reliable cover uploads.
const BLOG_COVER_IMAGE_MAX = [2000, 2000]
const BLOG_COVER_MAX_BYTES = 25 * 1024 * 1024

const BlogPostForm = ({ post, practice = {}, saving, onSave, onCancel, onPreview }) => {
  const { showToast } = useToast()
  const editorRef = useRef(null)
  const [title, setTitle] = useState(post?.title || "")
  const [subtitle, setSubtitle] = useState(post?.subtitle || "")
  const [slug, setSlug] = useState(post?.slug || "")
  const [tags, setTags] = useState(post?.tags || [])
  const [content, setContent] = useState(post?.content || "")
  const [photoUrl, setPhotoUrl] = useState(post?.photoUrl || null)
  const [stagedPhotoHandle, setStagedPhotoHandle] = useState(null)
  const [photoCleared, setPhotoCleared] = useState(false)
  const [publishedAtDate, setPublishedAtDate] = useState(
    () => blogPostDateInputValue(post?.publishedAt) || dayjs().format("YYYY-MM-DD")
  )
  const [inlinePickerOpen, setInlinePickerOpen] = useState(false)
  const [inlineUploading, setInlineUploading] = useState(false)

  const hasPhoto = Boolean(photoUrl || (post?.photoUrl && !photoCleared))
  const hasBasics = Boolean(title.trim() && content.trim())
  const canPublish = hasBasics && hasPhoto

  const effectiveSlug = useMemo(() => {
    const trimmed = slug.trim()
    if (trimmed) return trimmed.replace(/\s+/g, "-").toLowerCase()
    if (!title.trim()) return null
    return title
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
  }, [slug, title])

  const canPreview = hasBasics && Boolean(effectiveSlug || post?.slug)

  const buildPayload = () => ({
    id: post?.id,
    title: title.trim(),
    subtitle: subtitle.trim() || undefined,
    slug: slug.trim() || undefined,
    tags,
    content,
    photoHandle: stagedPhotoHandle,
    clearPhoto: photoCleared && Boolean(post?.photoUrl),
    publishedAt: publishedAtDate || undefined
  })

  const handleImageSelect = ({ handle, imageUrl }) => {
    setPhotoUrl(imageUrl)
    setStagedPhotoHandle(handle)
    setPhotoCleared(false)
  }

  const handleRemoveImage = (event) => {
    event.preventDefault()
    event.stopPropagation()
    setPhotoUrl(null)
    setStagedPhotoHandle(null)
    setPhotoCleared(true)
  }

  const handleSaveDraft = () => {
    if (!hasBasics) return
    onSave({ ...buildPayload(), published: false })
  }

  const handlePublish = () => {
    if (!canPublish) return
    onSave({ ...buildPayload(), published: true })
  }

  const handlePreview = () => {
    if (!canPreview || !onPreview) return
    onPreview(buildPayload())
  }

  const insertInlineImage = (url) => {
    if (!url) return
    editorRef.current?.insertImage(url)
  }

  const handleContentChange = (html) => {
    setContent(stripUnsafeInlineImagesFromHtml(html))
  }

  const handleInlineImageSelect = ({ handle, imageUrl }) => {
    const url = inlineImageUrlFromPicker({ handle, imageUrl })
    insertInlineImage(url)
    setInlinePickerOpen(false)
  }

  const handleInlineImageFiles = async (files) => {
    const file = files[0]
    if (!file) return
    setInlineUploading(true)
    showToast({ content: "Uploading image…", type: "info" })
    try {
      const { url } = await uploadFileToFilestack(file)
      insertInlineImage(url)
    } catch (error) {
      showToast({ content: error?.message || "Could not upload image. Try again.", type: "error" })
    } finally {
      setInlineUploading(false)
    }
  }

  const imagePicker = (
    <ImagePickerV2
      practice={practice}
      onImageSelect={handleImageSelect}
      currentImageUrl={photoUrl}
      showCurateTab={true}
      requireCrop={false}
      imageMax={BLOG_COVER_IMAGE_MAX}
      maxSize={BLOG_COVER_MAX_BYTES}
      accept={["image/*"]}
      trigger={
        <div className="relative w-full cursor-pointer overflow-hidden rounded-lg border border-builder-border bg-white">
          {photoUrl ? (
            <div className="relative h-60 w-full">
              <img src={photoUrl} alt="" className="size-full object-cover" />
              <button
                type="button"
                onClick={handleRemoveImage}
                className="absolute right-2 top-2 rounded-full bg-white px-2 py-1 text-sm font-bold text-builder-primary shadow-md hover:bg-gray-50"
                aria-label="Remove photo">
                ✕
              </button>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center px-3 py-8 transition-colors hover:bg-gray-50">
              <ArrowUpTrayIcon className="mb-2 size-8 text-builder-border" />
              <p className="text-sm font-bold text-builder-primary">Click to upload image</p>
              <p className="mt-1 text-xs text-builder-secondary">PNG, JPG or WEBP up to 25MB</p>
            </div>
          )}
        </div>
      }
    />
  )

  return (
    <div>
      <div className="space-y-6">
        <BlogPostFormField
          label="Post title"
          description="Create a powerful headline to capture attention and communicate your core message."
          required>
          <input
            type="text"
            className={inputClassName}
            placeholder="Enter your blog post title..."
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
          />
        </BlogPostFormField>

        <BlogPostFormField
          label="Subtitle"
          description="Add a brief subtitle to expand on your main message and provide additional context.">
          <input
            type="text"
            className={inputClassName}
            placeholder="Enter a subtitle..."
            value={subtitle}
            onChange={(e) => setSubtitle(e.target.value)}
            maxLength={200}
          />
        </BlogPostFormField>

        <BlogPostFormField
          label="URL slug"
          description="Optional. Leave blank to generate from the title. Used in your post URL: /blog/your-slug">
          <input
            type="text"
            className={inputClassName}
            placeholder="auto-generated from title if left blank"
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
          />
          {effectiveSlug && <p className="mt-2 text-xs text-builder-secondary">Preview path: /blog/{effectiveSlug}</p>}
        </BlogPostFormField>

        <BlogPostFormField
          label="Publish date"
          description="Shown on your public blog. You can set the original date for transferred posts. Defaults to today when you first publish.">
          <input
            type="date"
            className={inputClassName}
            value={publishedAtDate}
            onChange={(e) => setPublishedAtDate(e.target.value)}
          />
        </BlogPostFormField>

        <BlogPostFormField
          label="Primary photo"
          description="Upload a high-quality image to represent your post. This will be displayed as the cover image."
          required>
          {imagePicker}
        </BlogPostFormField>

        <BlogPostFormField
          label="Tags"
          description="Add relevant tags to help readers discover your content. Press Enter after each tag.">
          <BlogPostTagInput tags={tags} onChange={setTags} />
        </BlogPostFormField>

        <BlogPostFormField
          label="Content"
          description="Add your detailed blog post content. Use the formatting toolbar to style your text, add links, and create lists."
          required>
          <div className="overflow-hidden rounded-lg border border-builder-border bg-white">
            <QuillEditor
              ref={editorRef}
              value={content}
              onChange={handleContentChange}
              enableImages
              onRequestImage={() => setInlinePickerOpen(true)}
              onImageFiles={handleInlineImageFiles}
            />
          </div>
          <ImagePickerV2
            practice={practice}
            hideTrigger
            isOpen={inlinePickerOpen}
            onOpenChange={setInlinePickerOpen}
            requireCrop={false}
            imageMax={BLOG_INLINE_IMAGE_MAX}
            maxSize={BLOG_INLINE_MAX_BYTES}
            accept={["image/*"]}
            showCurateTab={true}
            onImageSelect={handleInlineImageSelect}
          />
        </BlogPostFormField>
      </div>

      <BlogPostActionBar
        saving={saving || inlineUploading}
        canSaveDraft={hasBasics}
        canPublish={canPublish}
        canPreview={canPreview}
        onSaveDraft={handleSaveDraft}
        onPreview={handlePreview}
        onPublish={handlePublish}
        onCancel={onCancel}
      />
    </div>
  )
}

export default BlogPostForm
